import React, { useEffect } from 'react';
import {
  Edit,
  SimpleForm,
  FileInput,
  ImageField,
  useUpdate,
  useNotify,
  useRedirect,
  Toolbar,
  SaveButton,
  required
} from 'react-admin';
import { RichTextInput } from 'ra-input-rich-text';
import { blocksToHtml, htmlToBlocks } from '../../../helpers/htmlUtils';
import { uploadImage } from '../../../config/services/strapi/strapi';

const getPreviewUrl = (file: any) => {
  const raw =
    file?.src ||
    file?.url ||
    file?.data?.attributes?.url ||
    file?.attributes?.url ||
    file?.formats?.thumbnail?.url ||
    file?.formats?.small?.url ||
    file?.formats?.medium?.url ||
    file?.formats?.large?.url ||
    '';

  if (!raw) return '';
  if (raw.startsWith('http') || raw.startsWith('blob:') || raw.startsWith('data:')) return raw;

  const base = process.env.REACT_APP_STRAPI_ENDPOINT || 'http://localhost:1337';
  return `${base}${raw}`;
};

const toInputFile = (file: any) => {
  if (!file) return null;

  const src = getPreviewUrl(file);
  const title = file?.title || file?.name || file?.alternativeText || 'imagen';
  if (!src) return null;

  return {
    ...file,
    src,
    title,
  };
};

const normalizeHeroForInput = (value: any) => {
  if (!value) return value;
  if (!Array.isArray(value)) {
    const normalized = toInputFile(value);
    return normalized || value;
  }

  return value.map((item: any) => toInputFile(item) || item);
};

const getJwtOrNotify = (notify: any, message: string) => {
  const jwt = localStorage.getItem('strapi_jwt');
  if (!jwt) {
    notify(message, { type: 'warning' });
    return null;
  }

  return jwt;
};

const uploadHero = async (hero: any, notify: any) => {
  if (!Array.isArray(hero) || !hero.length) return [];

  const jwt = getJwtOrNotify(notify, 'No autorizado para subir imágenes');
  if (!jwt) return [];

  const uploaded: any[] = [];
  for (const item of hero) {
    if (!item?.rawFile) continue;

    const up = await uploadImage(item.rawFile, jwt);
    if (up?.id) uploaded.push({ id: up.id });
  }

  return uploaded;
};

function PionerosEnergiaEdit(props: any) {
  const [update] = useUpdate();
  const redirect = useRedirect();
  const notify = useNotify();
  const resourceName = 'fg-pioneros-energias';

  useEffect(() => {
    document.title = 'Editar Pioneros Energía';
  }, []);

  const handleSubmit = async (data: any) => {
    try {
      const payload: Record<string, any> = {
        descripcion: data.descripcion,
        detalle: data.detalle,
      };

      const hero = await uploadHero(data.hero, notify);
      if (Array.isArray(data.hero) && data.hero.length && !hero.length) return;
      if (hero.length) payload.hero = hero;

      update(resourceName, { id: data.id, data: payload }, {
        onSuccess: () => {
          notify('Pioneros Energía actualizado', { type: 'success' });
          redirect('list', resourceName);
        },
        onError: (err: any) => {
          notify(`Error: ${err?.message || err}`, { type: 'error' });
        }
      });
    } catch (e: any) {
      console.error(e);
      notify(`Error al actualizar: ${e?.message || 'desconocido'}`, { type: 'error' });
    }
  };

  return (
    <Edit {...props}>
      <SimpleForm onSubmit={handleSubmit} toolbar={<Toolbar><SaveButton alwaysEnable /></Toolbar>}>
        <RichTextInput
          source="descripcion"
          label="Descripción"
          format={blocksToHtml}
          parse={htmlToBlocks}
          validate={[required()]}
          fullWidth
        />

        <RichTextInput
          source="detalle"
          label="Detalle"
          format={blocksToHtml}
          parse={htmlToBlocks}
          validate={[required()]}
          fullWidth
        />

        <FileInput source="hero" label="Hero (imágenes)" multiple format={normalizeHeroForInput}>
          <ImageField source="src" title="title" />
        </FileInput>
      </SimpleForm>
    </Edit>
  );
}

export default PionerosEnergiaEdit;
